<template>
    <Row class="CollateralListRow text-eidDarkBlue-75">
        <Cell
            v-for="({ name, field }) in columns"
            :key="name"
            :class="{'text-eidDangerRed':fieldHasEvaluationError(field)}"
            :data-test-id="`CollateralListRow__${name}Cell`"
        >
            <div v-tooltip="getFieldTooltipMessage(field)" class="inline-flex items-center">
                <EidIcon
                    v-if="fieldHasEvaluationError(field)"
                    class="text-eidDangerRed mr-1 text-xs"
                    data-test-id="CollateralListRow__errorIcon"
                    alt="error"
                    :icon-src="faExclamationTriangle"
                />
                <span data-test-id="CollateralListRow__value">
                    {{ getValue(field) }}
                </span>
            </div>
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { intersection } from 'lodash';

    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import { CollateralEntityDisplayField } from '@/modules/entity-details/models/CollateralEntityDisplayField.model';
    import { CollateralErrorType } from '@/modules/entity-details/models/CollateralErrorType.model';
    import { TableColumn } from '@/modules/entity-details/models/TableColumn.model';
    import { TprmFieldCollateralLimit } from '@/modules/entity-details/models/TprmFieldCollateralLimit.model';
    import {
        TprmFieldCollateralLimitKey,
    } from '@/modules/entity-details/models/TprmFieldCollateralLimitKey.model';
    import { currencyFormatter } from '@/modules/entity-details/utils/currencyFormatter';
    import {
        getCollateralShortError,
        getPossibleErrorTypes,
        getTypesFromErrors,
        isFieldValueMissing,
    } from '@/modules/entity-details/utils/getCollateralErrorAndType/getCollateralErrorAndType';
    import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    export default defineComponent({
        name: 'CollateralListRow',
        components: {
            Cell,
            EidIcon,
            Row,
        },
        props: {
            collateralLimitType: {
                type: Object as PropType<TprmFieldCollateralLimit>,
                required: true,
            },
            columns: {
                type: Array as PropType<TableColumn[]>,
                required: true,
            },
            nonCompliantMessages: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
                required: true,
            },
        },
        data() {
            return {
                faExclamationTriangle,
                cfcKeyAndEntityKeyMap: {
                    limitType: CollateralEntityDisplayField.coverageLimitType,
                    coverageLimit: CollateralEntityDisplayField.coverageLimit,
                    valuation: CollateralEntityDisplayField.coverageValuation,
                    deductible: CollateralEntityDisplayField.coverageDeductible,
                },
            };
        },
        computed: {
            categorizedEnumLabels(): CategorizedEnumLabels {
                // TODO(PRODUCT-18675): remove any after the migration to the newer version.
                return useDashboardStore().categorizedEnumLabels;
            },
            errorTypes(): CollateralErrorType[] {
                return getTypesFromErrors(this.nonCompliantMessages);
            },
        },
        methods: {
            getValue(field: TprmFieldCollateralLimitKey): unknown {
                const value = this.collateralLimitType[field];

                if (value == null) {
                    return '-';
                }

                if (field === 'limitType') {
                    return getEnumDefaultDisplayValue(
                        EnumCategories.collateralLimitType,
                        value,
                        this.categorizedEnumLabels,
                    );
                }

                if ([ 'coverageLimit', 'deductible' ].includes(field)) {
                    return currencyFormatter().format(Number(value));
                }

                return value;
            },
            getFieldErrorTypes(field: TprmFieldCollateralLimitKey): CollateralErrorType[] {
                const entityField = this.cfcKeyAndEntityKeyMap[field] || field;
                const possibleErrorTypesOfField = getPossibleErrorTypes(entityField);
                return intersection(this.errorTypes, possibleErrorTypesOfField);
            },
            fieldHasEvaluationError(field: TprmFieldCollateralLimitKey): boolean {
                return this.getFieldErrorTypes(field).length > 0 || this.isFieldValueMissing(field);
            },
            isFieldValueMissing(field: TprmFieldCollateralLimitKey): boolean {
                // Non-compliance is only known at the row level, not per field, so this fallback
                // can't tell whether limit/valuation/limitType were actually required - scoping it
                // to deductible avoids flagging fields that were legitimately never required.
                if (field !== 'deductible') {
                    return false;
                }
                return isFieldValueMissing(this.collateralLimitType[field], this.nonCompliantMessages.length > 0);
            },
            getFieldTooltipMessage(field: TprmFieldCollateralLimitKey): string | null {
                const errorTypes = this.getFieldErrorTypes(field);
                if (errorTypes.length > 0) {
                    return errorTypes.map(getCollateralShortError).join('\n');
                }
                return this.isFieldValueMissing(field)
                    ? getCollateralShortError(CollateralErrorType.fieldValueMissing)
                    : null;
            },
        },
    });
</script>
